/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Alert } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import Bio from 'src/components/PageTitle/Bio'
import { getSuspensionStatusText } from '../helpers/profileFormatting'
import { ProfileSuspension, ProfileUser } from '../types'
import { PROFILE_PHOTO_SIZE, MAX_CONTENT_WIDTH } from '../utils/constants'
import ProfileActionButtons from './ProfileActionButtons'
import Text from 'src/components/ts-components/Text'

interface ProfileHeaderProps {
  item: ProfileUser
  isSuspended: boolean
  suspensions: ProfileSuspension[] | undefined
  onEditClick: () => void
  onSettingsClick: () => void
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  item,
  isSuspended,
  suspensions,
  onEditClick,
  onSettingsClick,
}) => {
  const photoSrc =
    getImageThumbnailId(item) ||
    '/images/generic_placeholders/placeholder_player.png'

  return (
    <div
      css={css`
        background-color: ${colors.SECONDARY_400};
        padding: 24px 0;
        width: 100%;
      `}
    >
      <div
        css={css`
          max-width: ${MAX_CONTENT_WIDTH}px;
          margin: 0 auto;
          display: flex;
          align-items: flex-start;
          gap: 16px;
        `}
      >
        <img
          src={photoSrc}
          alt='Profile'
          css={css`
            width: ${PROFILE_PHOTO_SIZE}px;
            height: ${PROFILE_PHOTO_SIZE}px;
            border-radius: 50%;
            border: 2px solid ${colors.WHITE};
            object-fit: cover;
            flex-shrink: 0;
          `}
          onError={(e) => {
            const target = e.target as HTMLImageElement
            target.src = '/images/generic_placeholders/placeholder_player.png'
          }}
        />

        <div
          css={css`
            display: flex;
            flex-direction: column;
            flex: 1;
            min-width: 0;
            justify-content: center;
            align-self: stretch;
          `}
        >
          <Text
            variant='heading1'
            weight='semiBold'
            color={colors.NEUTRAL_0}
            style={`
            text-transform: capitalize;
          `}
          >
            {item.name_first} {item.name_last}
          </Text>
          <Bio
            birthDate={item.birth_date}
            height={item.height}
            weight={item.weight}
          />

          {isSuspended &&
            suspensions
              ?.filter((suspension) => suspension.is_active)
              .map((suspension) => (
                <Alert
                  key={String(suspension.id)}
                  showIcon
                  message={getSuspensionStatusText(suspension)}
                  type='error'
                  css={css`
                    margin-top: 16px;
                  `}
                />
              ))}
        </div>

        <ProfileActionButtons
          onEditClick={onEditClick}
          onSettingsClick={onSettingsClick}
        />
      </div>
    </div>
  )
}

export default ProfileHeader
